// ============================================================================
// Módulo Contrataciones — panel-super-admin.
// ============================================================================

import { crearTabla, mostrarToast } from '@plataforma/ui';
import { escaparHtml } from '@plataforma/utils';

import {
  listarSolicitudes,
  aprobarSolicitud,
  rechazarSolicitud,
  obtenerComprobante,
} from '../api/contrataciones.js';
import { publicarTienda } from '../api/publicacion.js';
import { exigirSuperAdmin } from '../guardas.js';

import { montarShell } from './shell.js';

const TONO_POR_ESTADO = {
  pendiente: 'alerta',
  aprobada: 'exito',
  aplicada: 'info',
  rechazada: 'peligro',
};

const ETIQUETA_ESTADO = {
  pendiente: 'Pendiente',
  aprobada: 'Aprobada',
  aplicada: 'Aplicada',
  rechazada: 'Rechazada',
};

function crearBadgeEstado(estado) {
  const badge = document.createElement('pf-badge-estado');
  const tono = TONO_POR_ESTADO[estado] ?? 'alerta';
  badge.setAttribute('tono', tono);
  badge.textContent = ETIQUETA_ESTADO[estado] ?? estado;
  return badge;
}

export async function vistaContrataciones(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/contrataciones');
  contenido.innerHTML = `
    <div class="pf-fila pf-fila--separada" style="margin-bottom: 16px;">
      <h1>Contrataciones</h1>
      <div>
        <button type="button" class="pf-boton pf-boton--secundario" id="pf-btn-pendientes" style="margin-right: 8px;">Pendientes</button>
        <button type="button" class="pf-boton pf-boton--secundario" id="pf-btn-todas">Todas</button>
      </div>
    </div>
    <div id="pf-contrataciones-tabla"><p class="pf-texto-suave">Cargando…</p></div>

    <pf-modal titulo="Detalle de solicitud" id="pf-modal-detalle"></pf-modal>
    <pf-modal titulo="Rechazar solicitud" id="pf-modal-rechazo"></pf-modal>
  `;

  let filtroEstado = 'pendiente';

  const cargarTabla = async () => {
    const destino = contenido.querySelector('#pf-contrataciones-tabla');
    destino.innerHTML = '<p class="pf-texto-suave">Cargando…</p>';

    const { data, error } = await listarSolicitudes(filtroEstado ? { estado: filtroEstado } : {});

    if (error) {
      const status = error.status ?? '—';
      const code = error.code ?? '—';
      const message = error.message ?? '—';
      const details = error.details ?? '—';
      const hint = error.hint ?? '—';
      destino.innerHTML = `
        <div class="pf-campo__error">
          <strong>Error al cargar solicitudes</strong><br>
          HTTP status: ${status}<br>
          code: ${code}<br>
          message: ${message}<br>
          details: ${details}<br>
          hint: ${hint}
        </div>
      `;
      return;
    }

    if (!data || data.length === 0) {
      destino.innerHTML = '<p class="pf-texto-suave">No hay solicitudes para este filtro.</p>';
      return;
    }

    const tabla = crearTabla({
      filaVacia: 'No hay solicitudes.',
      columnas: [
        { titulo: 'Empresa', render: (fila) => fila.empresa?.nombre ?? '—' },
        {
          titulo: 'Subdominio',
          render: (fila) => `${fila.empresa?.subdominio ?? '—'}.vimosdigital.com`,
        },
        { titulo: 'Plan', render: (fila) => fila.plan?.nombre ?? '—' },
        { titulo: 'Monto', render: (fila) => `S/ ${Number(fila.monto).toFixed(2)}` },
        { titulo: 'Estado', render: (fila) => crearBadgeEstado(fila.estado) },
        { titulo: 'Fecha', render: (fila) => new Date(fila.creado_en).toLocaleDateString('es-PE') },
        {
          titulo: 'Acciones',
          render: (fila) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pf-boton pf-boton--secundario';
            btn.textContent = 'Ver detalle';
            btn.addEventListener('click', () => abrirDetalle(fila));
            return btn;
          },
        },
      ],
      filas: data,
    });

    destino.innerHTML = '';
    destino.appendChild(tabla);
  };

  await cargarTabla();

  contenido.querySelector('#pf-btn-pendientes').addEventListener('click', async () => {
    filtroEstado = 'pendiente';
    await cargarTabla();
  });

  contenido.querySelector('#pf-btn-todas').addEventListener('click', async () => {
    filtroEstado = null;
    await cargarTabla();
  });

  async function abrirDetalle(solicitud) {
    const modalDetalle = contenido.querySelector('#pf-modal-detalle');
    const modalRechazo = contenido.querySelector('#pf-modal-rechazo');

    modalDetalle.innerHTML = `
      <div style="padding: 16px;">
        <p><strong>Empresa:</strong> ${escaparHtml(solicitud.empresa?.nombre ?? '—')}</p>
        <p><strong>Subdominio:</strong> ${escaparHtml(solicitud.empresa?.subdominio ?? '—')}.vimosdigital.com</p>
        <p><strong>Plan:</strong> ${escaparHtml(solicitud.plan?.nombre ?? '—')}</p>
        <p><strong>Monto:</strong> S/ ${Number(solicitud.monto).toFixed(2)}</p>
        <p><strong>Estado:</strong> ${ETIQUETA_ESTADO[solicitud.estado] ?? escaparHtml(solicitud.estado)}</p>
        <p><strong>Método de pago:</strong> ${escaparHtml(solicitud.metodo_pago)}</p>
        <p><strong>Enviado:</strong> ${new Date(solicitud.creado_en).toLocaleString('es-PE')}</p>
        ${solicitud.motivo_rechazo ? `<p><strong>Motivo rechazo:</strong> ${escaparHtml(solicitud.motivo_rechazo)}</p>` : ''}
        ${solicitud.revisado_en ? `<p><strong>Revisado:</strong> ${new Date(solicitud.revisado_en).toLocaleString('es-PE')}</p>` : ''}
        <p style="font-size:11px;color:#666;"><strong>Ruta storage:</strong> ${solicitud.evidencia_url ?? '—'}</p>
        <div style="margin-top: 16px;">
          <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-ver-comprobante">Ver comprobante</button>
        </div>
        ${
          solicitud.estado === 'pendiente'
            ? `
        <div style="margin-top: 16px; display: flex; gap: 8px;">
          <button type="button" class="pf-boton pf-boton--exito" id="pf-btn-aprobar">Aprobar</button>
          <button type="button" class="pf-boton pf-boton--peligro" id="pf-btn-rechazar">Rechazar</button>
        </div>`
            : ''
        }
        ${
          solicitud.estado === 'aprobada'
            ? `
        <div style="margin-top: 16px; display: flex; gap: 8px;">
          <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-publicar">Publicar tienda</button>
        </div>`
            : ''
        }
      </div>
    `;

    modalDetalle.abrir();

    const btnVerComprobante = modalDetalle.querySelector('#pf-btn-ver-comprobante');
    if (btnVerComprobante) {
      btnVerComprobante.addEventListener('click', async () => {
        const { data, error } = await obtenerComprobante(solicitud.id);
        if (error || !data?.url_firmada) {
          const status = error?.status ?? '—';
          const code = error?.code ?? '—';
          const message = error?.message ?? '—';
          const details = error?.details ?? '—';
          const hint = error?.hint ?? '—';
          mostrarToast(
            `No se pudo obtener el comprobante.\nHTTP status: ${status}\ncode: ${code}\nmessage: ${message}\ndetails: ${details}\nhint: ${hint}`,
            { tono: 'peligro' },
          );
          return;
        }
        window.open(data.url_firmada, '_blank');
      });
    }

    const btnAprobar = modalDetalle.querySelector('#pf-btn-aprobar');
    if (btnAprobar) {
      btnAprobar.addEventListener('click', async () => {
        const { error } = await aprobarSolicitud(solicitud.id);
        if (error) {
          mostrarToast(error.message || 'No se pudo aprobar la solicitud.', { tono: 'peligro' });
          return;
        }

        mostrarToast('Solicitud aprobada. Publicando tienda...', { tono: 'exito' });
        modalDetalle.cerrar();
        await cargarTabla();

        const { error: publicacionError } = await publicarTienda(solicitud.empresa_id);
        if (publicacionError) {
          mostrarToast(
            `Tienda aprobada, pero falló la publicación: ${publicacionError.message || 'Error desconocido'}`,
            { tono: 'peligro' },
          );
          return;
        }

        mostrarToast('Tienda publicada correctamente.', { tono: 'exito' });
      });
    }

    const btnPublicar = modalDetalle.querySelector('#pf-btn-publicar');
    if (btnPublicar) {
      btnPublicar.addEventListener('click', async () => {
        btnPublicar.disabled = true;
        btnPublicar.textContent = 'Publicando...';

        const { error: publicacionError } = await publicarTienda(solicitud.empresa_id);
        if (publicacionError) {
          mostrarToast(
            `No se pudo publicar la tienda: ${publicacionError.message || 'Error desconocido'}`,
            { tono: 'peligro' },
          );
          btnPublicar.disabled = false;
          btnPublicar.textContent = 'Publicar tienda';
          return;
        }

        mostrarToast('Tienda publicada correctamente.', { tono: 'exito' });
        btnPublicar.textContent = 'Publicada';
      });
    }

    const btnRechazar = modalDetalle.querySelector('#pf-btn-rechazar');
    if (btnRechazar) {
      btnRechazar.addEventListener('click', () => {
        modalDetalle.cerrar();
        modalRechazo.innerHTML = `
          <div style="padding: 16px;">
            <p class="pf-app__texto-suave">Ingresa el motivo del rechazo:</p>
            <textarea id="pf-motivo-rechazo" rows="4" style="width: 100%; margin-top: 8px; padding: 8px;"></textarea>
            <div style="margin-top: 16px; display: flex; gap: 8px; justify-content: flex-end;">
              <button type="button" class="pf-boton pf-boton--secundario" id="pf-btn-cancelar-rechazo">Cancelar</button>
              <button type="button" class="pf-boton pf-boton--peligro" id="pf-btn-confirmar-rechazo">Rechazar</button>
            </div>
          </div>
        `;
        modalRechazo.abrir();

        modalRechazo.querySelector('#pf-btn-cancelar-rechazo').addEventListener('click', () => {
          modalRechazo.cerrar();
        });

        modalRechazo
          .querySelector('#pf-btn-confirmar-rechazo')
          .addEventListener('click', async () => {
            const motivo = modalRechazo.querySelector('#pf-motivo-rechazo')?.value?.trim() || '';
            if (!motivo) {
              mostrarToast('El motivo de rechazo es obligatorio.', { tono: 'alerta' });
              return;
            }
            const { error } = await rechazarSolicitud(solicitud.id, motivo);
            if (error) {
              mostrarToast(error.message || 'No se pudo rechazar la solicitud.', {
                tono: 'peligro',
              });
              return;
            }
            mostrarToast('Solicitud rechazada.', { tono: 'exito' });
            modalRechazo.cerrar();
            await cargarTabla();
          });
      });
    }
  }
}
